/**
 * SigningPage
 *
 * Top-level (no auth, no Layout) page that recipients land on from the
 * signing email. URL: /a/arche_document_sign/sign/:token. The route is
 * registered before /a/:id/* in App.tsx so it doesn't dispatch through the
 * arche-interaction wrapper.
 *
 * Flow:
 *   token → consent → sign (review + fill fields) → submit → confirmation.
 *
 * Compliance shape: every action that produces an audit-trail event (view,
 * consent, sign, decline) sends IP + UA on the server side via the request
 * itself — we never trust client-supplied identity.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import api, {
  ApiError,
  DocumentSignCompletedFieldView,
  DocumentSignFieldResponse,
  DocumentSignSigningFieldSubmission,
  DocumentSignDeclineKind,
  SignatureMode,
  DocumentSignPublicBranding,
} from '@/lib/api';
import { addBreadcrumb, traceEnd, traceError, traceStart } from '@/lib/telemetry';
import { useAuth } from '@/auth';
import { HolderSwitcher, useHolderSwitcher } from '@/components/HolderSwitcher';
import {
  useSigningSession,
  useAcceptConsent,
  useSubmitFields,
  useSubmitSignature,
  useSigningBranding,
  useDeclineSignature,
  useSetDeliveryHolder,
} from '../hooks/useSigningSession';
import { ConsentDisclosure } from '../components/ConsentDisclosure';
import { AccessCodeGate } from '../components/AccessCodeGate';
import { tierOffersPaperCopyElection } from '../assuranceTier';
import { openDocumentInNewTab } from '../openDocument';
import {
  orderRequiredFields,
  stepPosition,
  revealField,
  isFieldComplete,
  firstIncompletePosition,
} from '../fieldNavigation';
import {
  PAPER_INK,
  PAPER_PLACEHOLDER_CLASS,
  fieldTextSize,
  fieldTextPadding,
} from '../paperSurface';
import { SignaturePad } from '../components/SignaturePad';
import { BrandedSigningFrame, BrandedCompletionMessage } from '../components/BrandedSigningFrame';
import { PdfPageView, usePdfDocument } from '../components/PdfPageView';
import { useRecipientSealedDownload } from '../hooks/useSealedDownload';

type Step = 'loading' | 'consent' | 'sign' | 'declining' | 'submitted' | 'declined' | 'error';

interface FieldFill {
  /** Either a string value (text/checkbox/date/name) or null */
  value?: string | null;
  /** Base64 PNG data URL — only set on signature/initial fields */
  dataUrl?: string | null;
  /** Set alongside `dataUrl` — only the pad knows which tab produced it. */
  signatureMode?: SignatureMode | null;
}

/** Max width (CSS px) at which a page is rendered. The signing view has no
 *  field sidebar, so the page grows to fill the content column (≈ the max-w-5xl
 *  width) for comfortable reading, shrinking to fit narrower viewports. */
const PAGE_RENDER_WIDTH = 1024;

export function SigningPage() {
  const { token } = useParams<{ token: string }>();
  const session = useSigningSession(token);
  const acceptConsent = useAcceptConsent(token ?? '');
  const submitFields = useSubmitFields(token ?? '');
  const submitSignature = useSubmitSignature(token ?? '');
  const declineSignature = useDeclineSignature(token ?? '');

  const [step, setStep] = useState<Step>('consent');
  const [fills, setFills] = useState<Record<string, FieldFill>>({});
  const [activeFieldId, setActiveFieldId] = useState<string | null>(null);
  const [declineReason, setDeclineReason] = useState('');
  // Which refusal the user is confirming. Set by whichever button opened the
  // `declining` step, so the confirm screen and the recorded event agree.
  const [declineKind, setDeclineKind] = useState<DocumentSignDeclineKind>('agreement');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const signSectionRef = useRef<HTMLElement | null>(null);

  // Announce step changes to assistive tech via aria-live below — also
  // emit a breadcrumb so a CloudWatch trace shows the recipient's path.
  useEffect(() => {
    addBreadcrumb('document-sign', `Signing step: ${step}`);
  }, [step]);

  const isLoading = session.isLoading;
  const data = session.data;

  // Seed `fills` with sensible defaults for auto-fillable fields the first
  // time the session lands. Without this, `signer_name` and `date_signed`
  // render empty (the renderers fall back to the recipient name / today's
  // date for DISPLAY but never populate `fills`), and the
  // `requiredFieldsRemaining` counter — which inspects `fills[fieldId].value`
  // — never decrements past those fields. End result: Finish Signing stays
  // disabled forever. The submit handler downstream already had matching
  // fallbacks for the eventual payload; this just hoists that knowledge
  // into the UI state so the counter and the renderers see it too.
  const fillSeededRef = useRef(false);
  useEffect(() => {
    if (!data || fillSeededRef.current) return;
    const seeded: Record<string, FieldFill> = {};
    const todayISO = new Date().toISOString().split('T')[0];
    for (const f of data.fields) {
      if (f.type === 'signer_name' && data.recipient.name) {
        seeded[f.fieldId] = { value: data.recipient.name };
      } else if (f.type === 'date_signed') {
        seeded[f.fieldId] = { value: todayISO };
      }
    }
    if (Object.keys(seeded).length > 0) {
      setFills((prev) => ({ ...seeded, ...prev }));
    }
    fillSeededRef.current = true;
  }, [data]);

  // Group fields by file so we can render each PDF + its overlay together.
  const fieldsByFile = useMemo(() => {
    const m: Record<string, DocumentSignFieldResponse[]> = {};
    if (!data) return m;
    for (const f of data.fields) {
      if (!m[f.fileId]) m[f.fileId] = [];
      m[f.fileId].push(f);
    }
    return m;
  }, [data]);

  // `isFieldComplete` is shared with the field navigator on purpose. Two
  // definitions of "done" would let the bar contradict itself — a count
  // insisting something is outstanding while Find next refuses to move.
  const requiredFieldsRemaining = useMemo(() => {
    if (!data) return 0;
    return data.fields.filter((f) => f.required && !isFieldComplete(f.type, fills[f.fieldId]))
      .length;
  }, [data, fills]);

  const handleAcceptConsent = useCallback(async () => {
    setSubmitError(null);
    const traceId = traceStart('document-sign:signing-consent');
    try {
      // The version the page rendered goes with the acceptance, so the server
      // can refuse if the text was superseded between load and click.
      await acceptConsent.mutateAsync(data?.consentDisclosureVersion);
      traceEnd(traceId);
      addBreadcrumb('document-sign', 'Recipient accepted consent');
      setStep('sign');
      // Move keyboard focus into the sign area so screen-reader users hear
      // the new state and don't have to hunt for the next interactive element.
      //
      // `preventScroll`, then an explicit scroll to the top. Focusing an
      // element scrolls it into view, and this one is the <main> INSIDE the
      // branded frame — so the default behaviour parked the viewport just
      // below the header and the signer had to scroll up to see whose document
      // they were signing. The branding is the first thing they should see,
      // and it is the sender's whole reason for configuring it.
      requestAnimationFrame(() => {
        signSectionRef.current?.focus({ preventScroll: true });
        window.scrollTo({ top: 0, behavior: 'auto' });
      });
    } catch (err) {
      traceError(traceId, err instanceof Error ? err : String(err));
      const apiErr = err instanceof ApiError ? err : null;
      if (apiErr?.status === 409 && apiErr.code === 'DISCLOSURE_UPDATED') {
        // The disclosure changed under us. Reload so the page shows the
        // current text, and say why the click did not go through.
        await session.refetch();
        setSubmitError(
          'The electronic-records disclosure was updated while this page was open. Please read the current version above and agree again.',
        );
        return;
      }
      if (apiErr?.status === 409 && apiErr.code === 'RECORD_ACCESS_REQUIRED') {
        await session.refetch();
        setSubmitError('Open the document as a PDF above before agreeing.');
        return;
      }
      setSubmitError(err instanceof Error ? err.message : 'Failed to record consent');
    }
  }, [acceptConsent, data?.consentDisclosureVersion, session]);

  const handleSubmit = useCallback(async () => {
    if (!data) return;
    setSubmitError(null);
    const traceId = traceStart('document-sign:signing-submit', {
      envelopeId: data.envelope.envelopeId,
      fieldCount: data.fields.length,
    });
    const payload: DocumentSignSigningFieldSubmission[] = data.fields.map((f) => {
      const fill = fills[f.fieldId];
      const isSignatureType = f.type === 'signature' || f.type === 'initial';
      if (isSignatureType) {
        return {
          fieldId: f.fieldId,
          signatureDataUrl: fill?.dataUrl ?? null,
          signatureMode: fill?.signatureMode ?? null,
        };
      }
      if (f.type === 'date_signed' && !fill?.value) {
        return { fieldId: f.fieldId, value: new Date().toISOString().split('T')[0] };
      }
      if (f.type === 'signer_name' && !fill?.value) {
        return { fieldId: f.fieldId, value: data.recipient.name ?? '' };
      }
      return { fieldId: f.fieldId, value: fill?.value ?? null };
    });

    try {
      await submitFields.mutateAsync(payload);
      const result = await submitSignature.mutateAsync();
      traceEnd(traceId, { envelopeCompleted: result.envelopeCompleted });
      addBreadcrumb('document-sign', 'Recipient submitted signature', {
        envelopeCompleted: result.envelopeCompleted,
      });
      setStep('submitted');
      setSubmitError(null);
    } catch (err) {
      traceError(traceId, err instanceof Error ? err : String(err));
      // Field state is preserved in `fills` — recipient can retry without
      // losing what they typed / drew. Mid-submit network failure is the
      // failure mode this most matters for.
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit signature');
    }
  }, [data, fills, submitFields, submitSignature]);

  /**
   * Fetch a fresh URL for the source PDF and open it. Going through the
   * server is the point: issuing the URL is what the server records as the
   * § 7001(c)(1)(C)(ii) demonstration. Opening the copy already presigned in
   * the session payload would show the signer the same document and witness
   * nothing.
   */
  const handleOpenDocument = useCallback(async () => {
    const firstFile = data?.files[0];
    if (!firstFile) throw new Error('This envelope has no document to open');
    // The tab-opening sequence lives in `openDocumentInNewTab` — it has two
    // non-obvious traps (a `noopener` feature string makes `window.open`
    // return null; the tab must be opened before the await) and both were
    // live here. Keeping it in a module means it can be tested directly
    // rather than only through a full page render.
    await openDocumentInNewTab(async () => {
      const { downloadUrl } = await api.getDocumentSignSigningFileUrl(
        token ?? '',
        firstFile.fileId,
      );
      return downloadUrl;
    });
    // The server records the retrieval before it answers, so this refetch
    // sees `recordAccessDemonstrated` true rather than racing the write.
    await session.refetch();
  }, [data, token, session]);

  const handleDeclineConfirm = useCallback(async () => {
    setSubmitError(null);
    const traceId = traceStart('document-sign:signing-decline');
    const paperRequest = declineKind === 'electronic_records';
    try {
      await declineSignature.mutateAsync({
        reason:
          declineReason ||
          (paperRequest ? 'Recipient requested a paper copy' : 'Recipient declined'),
        declineKind,
      });
      traceEnd(traceId);
      addBreadcrumb(
        'document-sign',
        paperRequest ? 'Recipient requested paper copy' : 'Recipient declined',
      );
      setStep('declined');
    } catch (err) {
      traceError(traceId, err instanceof Error ? err : String(err));
      setSubmitError(err instanceof Error ? err.message : 'Failed to decline');
    }
  }, [declineSignature, declineReason, declineKind]);

  if (!token) return <CenteredMessage title="Invalid link" />;

  if (isLoading) {
    return (
      <div className="max-w-3xl mx-auto p-6 space-y-3">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (session.error) {
    const apiErr = session.error instanceof ApiError ? session.error : null;
    if (apiErr?.status === 410) {
      // A recipient returning to their signing link after signing gets the
      // same post-sign view as the moment they signed — their completed copy
      // is downloadable there once every party has signed.
      if (apiErr.code === 'ALREADY_SIGNED') {
        return <SubmittedConfirmation token={token ?? ''} />;
      }
      // A completed envelope — the recipient followed the link in their
      // "document complete" email. Show the same download view a just-signed
      // recipient gets, so the link actually delivers the sealed PDF.
      if (apiErr.code === 'ENVELOPE_TERMINAL' && apiErr.envelopeStatus === 'completed') {
        return <SubmittedConfirmation token={token ?? ''} />;
      }
      // The middleware separates the remaining terminal-state codes — surface
      // specific copy so the recipient knows what happened. Default 410
      // message for anything we haven't enumerated.
      const { title, description } = (() => {
        switch (apiErr.code) {
          case 'TOKEN_EXPIRED':
            return {
              title: 'This signing link has expired',
              description: 'Signing links are good for 30 days. Ask the sender for a fresh link.',
            };
          case 'ALREADY_DECLINED':
            return {
              title: 'You already declined this envelope',
              description: 'The sender has been notified.',
            };
          case 'ENVELOPE_TERMINAL':
            return {
              title: 'This envelope is no longer active',
              description: apiErr.message ?? 'The envelope was voided, declined, or expired.',
            };
          default:
            return {
              title: 'This signing link is no longer active',
              description: apiErr.message ?? 'The envelope has expired or already finished.',
            };
        }
      })();
      return <CenteredMessage title={title} description={description} />;
    }
    // The sender set an access code and this recipient has not entered it.
    // The session is what the code protects, so nothing else is loaded yet;
    // the gate verifies the code and then this query is refetched.
    if (
      (apiErr?.status === 401 && apiErr.code === 'ACCESS_CODE_REQUIRED') ||
      (apiErr?.status === 423 && apiErr.code === 'ACCESS_CODE_LOCKED')
    ) {
      return (
        <AccessCodeGate
          token={token}
          locked={apiErr.status === 423}
          onVerified={() => {
            void session.refetch();
          }}
        />
      );
    }
    if (apiErr?.status === 403) {
      return (
        <CenteredMessage
          title="Not your turn yet"
          description="An earlier signer needs to finish first. We'll email you when it's your turn."
        />
      );
    }
    return (
      <CenteredMessage
        title="Signing link not found"
        description={apiErr?.message ?? 'This link is invalid or has been revoked.'}
      />
    );
  }

  if (!data) return null;

  if (step === 'submitted') {
    return <SubmittedConfirmation token={token} branding={data.branding} />;
  }
  if (step === 'declined') {
    return (
      <CenteredMessage
        title={
          declineKind === 'electronic_records'
            ? "You've asked for a paper copy"
            : "You've declined to sign"
        }
        description={
          declineKind === 'electronic_records'
            ? 'The sender has been told you would rather not sign electronically, and that you have not refused the agreement. You can close this page.'
            : 'The sender has been notified. You can close this page.'
        }
      />
    );
  }

  return (
    <BrandedSigningFrame
      branding={data.branding}
      // No colour class: BrandedSigningFrame owns the header foreground, and a
      // class here would override the sender's chosen colour with theme grey.
      subtitle={<p className="text-xs truncate">{data.envelope.title}</p>}
      right={
        <span className="text-xs shrink-0">
          Signing as {data.recipient.name ?? data.recipient.email ?? 'recipient'}
        </span>
      }
    >
      {step === 'consent' && (
        <main className="max-w-5xl mx-auto px-4 py-8">
          {/* § 7001(c)(1)(D). The consent step always runs on load; this
              tells a returning signer WHY they are being asked again. */}
          {data.requiresReconsent && (
            <p
              className="max-w-2xl mx-auto mb-6 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-900 dark:text-amber-200"
              role="status"
            >
              The electronic-records disclosure has changed since you last agreed to it, in a way
              that affects what you need to access your documents. Please read the current version
              and agree again before signing.
            </p>
          )}
          <ConsentDisclosure
            envelopeTitle={data.envelope.title}
            ownerName={data.envelope.ownerName}
            disclosureText={data.consentDisclosure}
            onOpenDocument={handleOpenDocument}
            accessDemonstrated={data.recordAccessDemonstrated ?? false}
            requiresAccessDemonstration={data.requiresPreConsentAccess ?? false}
            offerPaperCopy={tierOffersPaperCopyElection(data.assuranceTier ?? 'standard')}
            onAccept={handleAcceptConsent}
            onDecline={() => {
              setDeclineKind('agreement');
              setStep('declining');
            }}
            onDeclineElectronic={() => {
              setDeclineKind('electronic_records');
              setStep('declining');
            }}
            pending={acceptConsent.isPending}
          />
          {submitError && <p className="text-sm text-destructive mt-4">{submitError}</p>}
        </main>
      )}

      {step === 'sign' && (
        <SignSection
          data={data}
          fields={data.fields}
          fieldsByFile={fieldsByFile}
          fills={fills}
          activeFieldId={activeFieldId}
          setActiveFieldId={setActiveFieldId}
          setFills={setFills}
          requiredFieldsRemaining={requiredFieldsRemaining}
          submitting={submitFields.isPending || submitSignature.isPending}
          submitError={submitError}
          onSubmit={handleSubmit}
          onDecline={() => {
            // Past the consent gate the only remaining refusal is of the
            // document: they already accepted electronic records to get here.
            setDeclineKind('agreement');
            setStep('declining');
          }}
          message={data.envelope.message}
          sectionRef={signSectionRef}
        />
      )}

      {step === 'declining' && (
        <main className="max-w-2xl mx-auto px-4 py-8 space-y-4">
          <h2 className="text-xl font-semibold">
            {declineKind === 'electronic_records' ? 'Request a paper copy' : 'Decline to sign'}
          </h2>
          <p className="text-sm text-muted-foreground">
            {declineKind === 'electronic_records'
              ? 'Tell the sender how to reach you with a paper copy. You are declining to sign electronically, not declining the agreement — the sender will be told that. This signing link will close and no further signatures will be requested through Fias.'
              : "Tell the sender briefly why you're declining. The envelope will be marked declined and no further signatures will be requested."}
          </p>
          <Textarea
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            placeholder="Reason (optional)"
            maxLength={1000}
            rows={4}
          />
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setStep('sign')}
              disabled={declineSignature.isPending}
            >
              Back
            </Button>
            <Button onClick={handleDeclineConfirm} disabled={declineSignature.isPending}>
              {declineKind === 'electronic_records' ? 'Request paper copy' : 'Confirm decline'}
            </Button>
          </div>
          {submitError && <p className="text-sm text-destructive">{submitError}</p>}
        </main>
      )}
    </BrandedSigningFrame>
  );
}

interface SignSectionProps {
  data: NonNullable<ReturnType<typeof useSigningSession>['data']>;
  fields: DocumentSignFieldResponse[];
  fieldsByFile: Record<string, DocumentSignFieldResponse[]>;
  fills: Record<string, FieldFill>;
  activeFieldId: string | null;
  setActiveFieldId: (id: string | null) => void;
  setFills: React.Dispatch<React.SetStateAction<Record<string, FieldFill>>>;
  requiredFieldsRemaining: number;
  submitting: boolean;
  submitError: string | null;
  message: string | null;
  onSubmit: () => void;
  onDecline: () => void;
  sectionRef?: React.RefObject<HTMLElement | null>;
}

function SignSection({
  data,
  fields,
  fieldsByFile,
  fills,
  activeFieldId,
  setActiveFieldId,
  setFills,
  requiredFieldsRemaining,
  submitting,
  submitError,
  message,
  onSubmit,
  onDecline,
  sectionRef,
}: SignSectionProps) {
  const activeField = fields.find((f) => f.fieldId === activeFieldId) ?? null;
  const padOpen =
    !!activeField && (activeField.type === 'signature' || activeField.type === 'initial');

  // Earlier signers' completed fields (sequential envelopes) — rendered as
  // read-only overlays so this signer sees what has already been signed.
  const completedFieldsByFile = useMemo(() => {
    const m: Record<string, DocumentSignCompletedFieldView[]> = {};
    for (const f of data.completedFields ?? []) {
      if (!m[f.fileId]) m[f.fileId] = [];
      m[f.fileId].push(f);
    }
    return m;
  }, [data]);

  // Flash the action bar when the last required field lands, so the signer
  // notices the Finish button has gone live.
  //
  // This used to SCROLL to the bar, because the bar lived above the documents
  // and was off-screen by the time anyone finished. It is pinned to the
  // viewport now, so there is nowhere to scroll it to — which also retires the
  // guard that stopped the scroll firing mid-keystroke (a text field counts as
  // complete at one character, so the first letter typed into the last field
  // used to yank the page away from the signer).
  /**
   * The fields the arrows walk, in reading order, and where the signer is in
   * that walk. `0` means "not started", so the bar opens at `0 of 3`.
   *
   * Position is not derived from what is filled: it is where the signer last
   * navigated to. The count of what remains is stated separately, a few pixels
   * away, and conflating the two would make one number contradict the other.
   */
  const navigableFields = useMemo(
    () =>
      orderRequiredFields(
        data.files.map((f) => f.fileId),
        fieldsByFile,
      ),
    [data.files, fieldsByFile],
  );
  const [navPosition, setNavPosition] = useState(0);

  /**
   * The field a navigation just landed on, highlighted briefly.
   *
   * Without it, pressing a navigation control when the target is ALREADY on
   * screen does nothing a signer can see — the page does not move, and the
   * natural conclusion is that the button is broken. A flash makes every press
   * produce a response, whether or not it also scrolls, and it answers the
   * question the scroll alone does not: WHICH of these boxes did it mean?
   *
   * Preferred over disabling the control when the target is in view: a greyed
   * button with no stated reason is a harder puzzle than a still page, and
   * "in view" has no honest threshold on a phone mid-momentum-scroll.
   */
  const [flashFieldId, setFlashFieldId] = useState<string | null>(null);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    },
    [],
  );
  const flashField = useCallback((fieldId: string) => {
    setFlashFieldId(fieldId);
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => setFlashFieldId(null), 1200);
  }, []);

  const goToField = useCallback(
    async (direction: 1 | -1) => {
      const next = stepPosition(navPosition, navigableFields.length, direction);
      if (next === 0) return;
      const target = navigableFields[next - 1];
      // Reveal and focus only — deliberately NOT `setActiveFieldId`. Activating
      // a signature field opens the signature pad, and a modal covering the
      // page is the opposite of what someone asked for when they pressed "go
      // to the next field". The focused control is one keystroke from opening
      // it if that is what they want.
      //
      // The counter and the flash both wait for the reveal to SUCCEED. A page
      // that never paints would otherwise leave the readout claiming a
      // position the signer was never taken to, and start a 1.2s highlight on
      // a field that may not mount until after it has expired — losing the
      // flash in exactly the slow-paint case it exists for.
      if (await revealField(target.fieldId, `${target.fileId}:${target.page}`)) {
        setNavPosition(next);
        flashField(target.fieldId);
      }
    },
    [navPosition, navigableFields, flashField],
  );

  /**
   * "Find next" — always the FIRST field still needing action, never "the one
   * after where I am".
   *
   * That makes it stateless: it means the same thing however many times it is
   * pressed, and it cannot leave an outstanding field behind the way a
   * forward-only cursor can until it has wrapped the whole way round. Filling
   * a field drops it from the set, so repeated presses walk the document top
   * to bottom on their own — press, fill, press, fill.
   *
   * It shares the stepper's counter rather than keeping its own position, so
   * the two controls agree about where the signer is and the arrows carry on
   * from wherever this left them.
   */
  const firstOutstanding = firstIncompletePosition(navigableFields, fills);
  const goToFirstOutstanding = useCallback(async () => {
    if (firstOutstanding === 0) return;
    const target = navigableFields[firstOutstanding - 1];
    if (await revealField(target.fieldId, `${target.fileId}:${target.page}`)) {
      setNavPosition(firstOutstanding);
      flashField(target.fieldId);
    }
  }, [firstOutstanding, navigableFields, flashField]);

  const finishBarRef = useRef<HTMLDivElement>(null);
  const prevRemainingRef = useRef(requiredFieldsRemaining);
  const [finishReady, setFinishReady] = useState(false);
  useEffect(() => {
    const prev = prevRemainingRef.current;
    prevRemainingRef.current = requiredFieldsRemaining;
    if (prev > 0 && requiredFieldsRemaining === 0) {
      setFinishReady(true);
      const timer = setTimeout(() => setFinishReady(false), 2600);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [requiredFieldsRemaining]);

  return (
    <main
      ref={sectionRef}
      tabIndex={-1}
      className="max-w-5xl mx-auto px-4 py-6 space-y-6 outline-none"
      aria-label="Sign documents"
    >
      <div className="sr-only" aria-live="polite" role="status">
        {submitting
          ? 'Submitting your signature'
          : requiredFieldsRemaining > 0
            ? `${requiredFieldsRemaining} required field${
                requiredFieldsRemaining === 1 ? '' : 's'
              } remaining`
            : 'All required fields complete. Ready to submit.'}
      </div>
      {message && (
        <section className="border rounded-md p-4 bg-muted/40">
          <h2 className="text-sm font-semibold mb-1">Message from {data.envelope.ownerName}</h2>
          <p className="text-sm whitespace-pre-wrap">{message}</p>
        </section>
      )}

      {data.recipient.deliveryEligible && (
        <DeliveryHolderSection
          recipientId={data.recipient.recipientId}
          initialHolderId={data.recipient.deliveryHolderId}
        />
      )}

      {submitError && (
        <p className="text-sm text-destructive" role="alert">
          {submitError}
        </p>
      )}

      {data.files.map((file) => (
        <FileSigningView
          key={file.fileId}
          file={file}
          fields={fieldsByFile[file.fileId] ?? []}
          completedFields={completedFieldsByFile[file.fileId] ?? []}
          fills={fills}
          setFills={setFills}
          onActivateField={setActiveFieldId}
          flashFieldId={flashFieldId}
        />
      ))}

      {/*
        The signing actions, pinned to the bottom of the viewport.

        They used to sit in a bar ABOVE the documents, which put them off-screen
        for the whole of a long document — the signer scrolled to the end, found
        nothing, and had to scroll back up. The copy made it worse by saying
        "finish below" while the button sat beside it.

        Pinned, the controls are in the same place on every screen size and at
        every scroll position, which is the property that actually matters on a
        phone. They are ALWAYS PRESENT and disabled until the document is ready,
        rather than appearing on completion: a control that materialises
        unannounced is easy to miss, and a visible-but-disabled one tells the
        signer what is left to do while they are doing it.

        `sticky` rather than `fixed` so it participates in layout and cannot
        overlap the last page of the document; the bottom padding on <main>
        reserves its height.
      */}
      <div
        ref={finishBarRef}
        className={`sticky bottom-0 -mx-4 border-t bg-background/95 px-4 py-3 backdrop-blur transition-shadow supports-[backdrop-filter]:bg-background/80 ${
          finishReady ? 'shadow-[0_-4px_16px_-4px_hsl(var(--primary)/0.45)]' : ''
        }`}
        style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
      >
        {/*
          Stacked on a phone, side by side from `sm` up.

          `flex-wrap` alone did not survive portrait: the left block carried
          `flex-1`, whose basis is 0, so it never grew wide enough to push the
          buttons onto a second line — it shrank instead, and the nav group
          inside it (correctly `shrink-0`) overflowed underneath Decline and
          Finish. A column below `sm` is unambiguous, and it gives the two
          actions a full-width row of their own, which is the better thumb
          target anyway.
        */}
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <div className="min-w-0 sm:flex-1">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <p className="text-sm font-medium">
                {requiredFieldsRemaining > 0
                  ? `${requiredFieldsRemaining} required field${
                      requiredFieldsRemaining === 1 ? '' : 's'
                    } left to complete`
                  : 'All required fields complete'}
              </p>

              {/*
                TWO controls, deliberately unequal, because they answer two
                different questions.

                "Find next" is the one nearly everyone wants: take me to the
                thing I still have to do. Labelled, sitting immediately after
                the count it completes ("5 left to complete → Find next"), and
                it always lands on the FIRST outstanding field rather than the
                one after the cursor. That makes it stateless — same result
                however often it is pressed — and means it can never skip a gap
                or need to wrap. Press, fill, press, fill walks the document.

                The arrows are for REVIEWING: they step through every required
                field in order, including ones already filled, which is the
                whole point when you want to re-read what you entered. Quieter
                on purpose — ghost, icon-only — so they compete with neither
                Find next nor Finish signing. They stay live when everything is
                complete, because that is exactly when reviewing matters.

                Both are optional. The signer can still scroll and read every
                page, and neither changes what Finish requires.
              */}
              {navigableFields.length > 0 && (
                <div className="flex shrink-0 items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8"
                    onClick={() => void goToFirstOutstanding()}
                    disabled={submitting || firstOutstanding === 0}
                    title={
                      firstOutstanding === 0
                        ? 'Nothing left to fill in'
                        : 'Jump to the first field still needing you'
                    }
                  >
                    <Search className="mr-1.5 h-3.5 w-3.5" />
                    Find next
                  </Button>

                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => void goToField(-1)}
                      disabled={submitting}
                      aria-label="Go to the previous field"
                    >
                      <ChevronUp className="h-4 w-4" />
                    </Button>
                    <span className="min-w-[3.25rem] text-center text-xs tabular-nums text-muted-foreground">
                      {navPosition} of {navigableFields.length}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => void goToField(1)}
                      disabled={submitting}
                      aria-label="Go to the next field"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
            {/* Intent restated AT the control that executes it. The consent
                step established willingness to sign electronically; this is
                the moment the signer adopts the document, and § 7006(5) turns
                on intent at exactly that point — so this line has to travel
                with the button, not stay behind at the top of the page. */}
            {requiredFieldsRemaining === 0 && (
              <p className="mt-1 text-xs text-muted-foreground">
                By selecting Finish signing, you adopt this document with the signature and
                information above, intending it to have the same effect as signing on paper.
              </p>
            )}
          </div>
          {/* Each button takes half the row on a phone — a full-width pair is
              easier to hit than two right-aligned ones — and reverts to
              intrinsic width once there is room beside the text. */}
          <div className="flex gap-2 sm:shrink-0">
            <Button
              variant="outline"
              className="flex-1 sm:flex-none"
              onClick={onDecline}
              disabled={submitting}
            >
              Decline
            </Button>
            <Button
              onClick={onSubmit}
              disabled={submitting || requiredFieldsRemaining > 0}
              className={`flex-1 sm:flex-none ${finishReady ? 'animate-pulse' : ''}`}
            >
              {submitting ? 'Submitting…' : 'Finish signing'}
            </Button>
          </div>
        </div>
      </div>

      <SignaturePad
        open={padOpen}
        purpose={activeField?.type === 'initial' ? 'initial' : 'signature'}
        defaultName={
          activeField?.type === 'initial'
            ? deriveInitials(data.recipient.name ?? '')
            : (data.recipient.name ?? '')
        }
        onCancel={() => setActiveFieldId(null)}
        onSave={(dataUrl, signatureMode) => {
          if (!activeField) return;
          setFills((prev) => ({
            ...prev,
            [activeField.fieldId]: { ...prev[activeField.fieldId], dataUrl, signatureMode },
          }));
          setActiveFieldId(null);
        }}
      />
    </main>
  );
}

function FileSigningView({
  file,
  fields,
  completedFields,
  fills,
  setFills,
  onActivateField,
  flashFieldId,
}: {
  file: NonNullable<ReturnType<typeof useSigningSession>['data']>['files'][number];
  fields: DocumentSignFieldResponse[];
  completedFields: DocumentSignCompletedFieldView[];
  fills: Record<string, FieldFill>;
  setFills: React.Dispatch<React.SetStateAction<Record<string, FieldFill>>>;
  onActivateField: (id: string | null) => void;
  /** Field the navigator just landed on, highlighted briefly. */
  flashFieldId?: string | null;
}) {
  const { doc, error, loading, stalled } = usePdfDocument(file.downloadUrl);
  const totalPages = file.pageCount ?? doc?.numPages ?? 0;
  const pages = totalPages > 0 ? Array.from({ length: totalPages }, (_, i) => i + 1) : [];

  const listRef = useRef<HTMLDivElement>(null);
  const [pageWidth, setPageWidth] = useState(PAGE_RENDER_WIDTH);

  // Size pages to the content column: grow up to PAGE_RENDER_WIDTH to fill the
  // available width, and shrink to fit narrower viewports so the document is
  // always fully visible left↔right (no horizontal scrollbar). useLayoutEffect
  // measures before paint so the larger initial width never overflows for a
  // frame. Fields are placed as a fraction of the rendered page, so a width
  // change re-flows them without moving anything.
  useLayoutEffect(() => {
    const node = listRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      const available = Math.floor(node.clientWidth) - 1;
      if (available > 0) setPageWidth(Math.min(PAGE_RENDER_WIDTH, available));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  if (error) {
    return (
      <section className="border rounded-md p-4 text-sm text-destructive">
        Failed to load {file.fileName}: {error}
      </section>
    );
  }

  return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold">{file.fileName}</h3>
      <div ref={listRef} className="space-y-4">
        {pages.map((page) => {
          const pageFields = fields.filter((f) => f.page === page);
          const pageCompleted = completedFields.filter((f) => f.page === page);
          return (
            <PdfPageView
              key={page}
              pdfDoc={doc}
              pageNumber={page}
              width={pageWidth}
              pageKey={`${file.fileId}:${page}`}
              documentLoading={loading}
              documentStalled={stalled}
            >
              {(rendered) => (
                <>
                  {pageCompleted.map((field) => (
                    <CompletedFieldOverlay key={field.fieldId} field={field} rendered={rendered} />
                  ))}
                  {pageFields.map((field) => {
                    const fill = fills[field.fieldId];
                    return (
                      <SigningFieldOverlay
                        key={field.fieldId}
                        field={field}
                        fill={fill}
                        rendered={rendered}
                        flashing={flashFieldId === field.fieldId}
                        onActivate={() => onActivateField(field.fieldId)}
                        onValueChange={(value) =>
                          setFills((prev) => ({
                            ...prev,
                            [field.fieldId]: { ...prev[field.fieldId], value },
                          }))
                        }
                      />
                    );
                  })}
                </>
              )}
            </PdfPageView>
          );
        })}
      </div>
    </section>
  );
}

/**
 * Read-only overlay for an EARLIER signer's completed field (sequential
 * envelopes). The source PDF is only flattened with signatures once the
 * whole envelope completes, so these overlays are how a later signer sees
 * "who already signed" while the document is mid-flight. Deliberately
 * non-interactive and visually settled (neutral border, no fill tint).
 * Exported for its behavioral test.
 */
export function CompletedFieldOverlay({
  field,
  rendered,
}: {
  field: DocumentSignCompletedFieldView;
  rendered: { width: number; height: number };
}) {
  const boxHeight = field.h * rendered.height;
  const style: React.CSSProperties = {
    position: 'absolute',
    left: field.x * rendered.width,
    top: field.y * rendered.height,
    width: field.w * rendered.width,
    height: boxHeight,
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    borderRadius: 4,
    // Drawn on the PDF canvas, which is white in every theme — so the ink is
    // fixed rather than inherited. See `paperSurface.ts`.
    color: PAPER_INK,
  };

  if (field.type === 'signature' || field.type === 'initial') {
    return (
      <div style={{ ...style, justifyContent: 'center' }} aria-label={`${field.type}, signed`}>
        {field.signatureUrl ? (
          <img
            src={field.signatureUrl}
            alt="Signature"
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        ) : null}
      </div>
    );
  }

  if (field.type === 'checkbox') {
    return (
      <div style={{ ...style, justifyContent: 'center' }} aria-label="checkbox, completed">
        <input type="checkbox" checked={field.value === 'true'} readOnly disabled />
      </div>
    );
  }

  return (
    <div
      style={{
        ...style,
        padding: fieldTextPadding(boxHeight),
        fontSize: fieldTextSize(boxHeight),
      }}
      aria-label={`${field.type}, completed`}
    >
      {field.value ?? ''}
    </div>
  );
}

function SigningFieldOverlay({
  field,
  fill,
  rendered,
  onActivate,
  onValueChange,
  flashing = false,
}: {
  field: DocumentSignFieldResponse;
  fill: FieldFill | undefined;
  rendered: { width: number; height: number };
  onActivate: () => void;
  onValueChange: (value: string) => void;
  /** Briefly ringed after a navigation lands here. */
  flashing?: boolean;
}) {
  const left = field.x * rendered.width;
  const top = field.y * rendered.height;
  const width = field.w * rendered.width;
  const height = field.h * rendered.height;

  const filled =
    field.type === 'signature' || field.type === 'initial' ? !!fill?.dataUrl : !!fill?.value;

  // Boxes are a fraction of the rendered page, so they shrink with the
  // viewport; the text has to shrink with them or it overflows its border in
  // portrait. See `fieldTextSize`.
  const textPx = fieldTextSize(height);
  const padPx = fieldTextPadding(height);

  const baseStyle: React.CSSProperties = {
    position: 'absolute',
    left,
    top,
    width,
    height,
    border: filled ? '1px solid #16a34a' : '1px dashed #2563eb',
    background: filled ? 'rgba(22,163,74,0.12)' : 'rgba(37,99,235,0.10)',
    borderRadius: 4,
    // The field sits on the PDF page, not on the app surface. Inheriting
    // `text-foreground` made every value white on white paper in dark mode —
    // typed text as much as pre-filled. See `paperSurface.ts`.
    color: PAPER_INK,
    // A ring rather than a border change, so the flash cannot shift the
    // field's geometry — these are absolutely positioned against a rendered
    // page, and a pixel of reflow would look like the overlay drifting off
    // its box.
    boxShadow: flashing ? '0 0 0 3px rgba(37, 99, 235, 0.55)' : undefined,
    transition: 'box-shadow 150ms ease-out',
  };

  if (field.type === 'signature' || field.type === 'initial') {
    return (
      <button
        type="button"
        data-ds-field-id={field.fieldId}
        style={{ ...baseStyle, fontSize: textPx }}
        className="flex items-center justify-center hover:bg-blue-200/40"
        onClick={onActivate}
        aria-label={`${field.type} field, ${filled ? 'signed' : 'click to sign'}`}
      >
        {fill?.dataUrl ? (
          <img
            src={fill.dataUrl}
            alt=""
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        ) : (
          <span>{field.type === 'signature' ? 'Sign here' : 'Initial'}</span>
        )}
      </button>
    );
  }

  if (field.type === 'checkbox') {
    return (
      <label
        data-ds-field-id={field.fieldId}
        style={{ ...baseStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <input
          type="checkbox"
          checked={fill?.value === 'true'}
          onChange={(e) => onValueChange(e.target.checked ? 'true' : 'false')}
          aria-label={`${field.type} field`}
        />
      </label>
    );
  }

  if (field.type === 'date_signed') {
    return (
      <div
        data-ds-field-id={field.fieldId}
        tabIndex={-1}
        style={{
          ...baseStyle,
          padding: padPx,
          fontSize: textPx,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {fill?.value || new Date().toISOString().split('T')[0]}
      </div>
    );
  }

  if (field.type === 'signer_name') {
    // Editable input pre-seeded by the page-level effect with the recipient's
    // name. Editable rather than a read-only display so an email-only signer
    // (no name on the recipient row) can fill the field at sign time —
    // otherwise the field is stuck empty and Finish Signing stays disabled.
    return (
      <input
        type="text"
        data-ds-field-id={field.fieldId}
        value={fill?.value ?? ''}
        onChange={(e) => onValueChange(e.target.value)}
        className={PAPER_PLACEHOLDER_CLASS}
        style={{ ...baseStyle, padding: padPx, fontSize: textPx }}
        aria-label={`name field${field.required ? ', required' : ''}`}
      />
    );
  }

  // email / company / title / number / text — a free-text input. Email and
  // number get the matching input type for better keyboards / validation.
  // The placeholder doubles as a label so the recipient can tell at a glance
  // what each box is asking for — without it, blank boxes are
  // indistinguishable across types.
  const inputType = field.type === 'email' ? 'email' : field.type === 'number' ? 'number' : 'text';
  const placeholder = FIELD_PLACEHOLDER[field.type] ?? '';
  return (
    <input
      type={inputType}
      data-ds-field-id={field.fieldId}
      value={fill?.value ?? ''}
      onChange={(e) => onValueChange(e.target.value)}
      placeholder={placeholder}
      className={PAPER_PLACEHOLDER_CLASS}
      style={{ ...baseStyle, padding: padPx, fontSize: textPx }}
      aria-label={`${field.type} field${field.required ? ', required' : ''}`}
    />
  );
}

/**
 * Placeholder text for the text-collection field types — surfaces what
 * each box is asking for without needing an external label. Keyed by the
 * field type so the renderer just looks it up. Limited to the types the
 * fall-through input actually handles.
 */
const FIELD_PLACEHOLDER: Partial<Record<DocumentSignFieldResponse['type'], string>> = {
  email: 'Email',
  company: 'Company',
  title: 'Title',
  number: 'Number',
  text: 'Text',
};

/**
 * Optional "where should we deliver your signed copy?" step. Surfaced only when
 * the bootstrap response marks the recipient `deliveryEligible` (i.e. they map
 * to a Fias user). Choosing a holder saves an owned copy of the completed PDF
 * into that Vault workspace; skipping it is fine — the signer still gets the
 * emailed copy and the token download. The POST is authenticated, so if the
 * visitor is browsing anonymously we surface a log-in prompt instead.
 */
function DeliveryHolderSection({
  recipientId,
  initialHolderId,
}: {
  recipientId: string;
  initialHolderId: string | null;
}) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const switcher = useHolderSwitcher({ defaultToPersonal: true });
  const setDeliveryHolder = useSetDeliveryHolder(recipientId);
  const [savedHolderId, setSavedHolderId] = useState<string | null>(initialHolderId);

  const handleChange = useCallback(
    (holderId: string | null) => {
      switcher.setSelectedHolderId(holderId);
      if (!holderId) return;
      setDeliveryHolder.mutate(holderId, {
        onSuccess: (res) => setSavedHolderId(res.recipient.deliveryHolderId),
      });
    },
    [switcher, setDeliveryHolder],
  );

  return (
    <section className="border rounded-md p-4 space-y-3">
      <div>
        <h2 className="text-sm font-semibold">Where should we deliver your signed copy?</h2>
        <p className="text-xs text-muted-foreground">
          Optional — you'll receive the completed document by email regardless.
        </p>
      </div>

      {authLoading ? (
        <p className="text-sm text-muted-foreground">Checking your account…</p>
      ) : !isAuthenticated ? (
        <p className="text-sm text-muted-foreground">
          <a href="/login" className="underline font-medium">
            Log in
          </a>{' '}
          to save a copy to your Vault, or continue and just receive the email.
        </p>
      ) : (
        <>
          <HolderSwitcher
            selectedHolderId={switcher.selectedHolderId ?? savedHolderId}
            onChange={(holderId) => handleChange(holderId)}
            hideCreate
          />
          {setDeliveryHolder.isPending && (
            <p className="text-xs text-muted-foreground">Saving your choice…</p>
          )}
          {setDeliveryHolder.isError && (
            <p className="text-xs text-destructive" role="alert">
              {setDeliveryHolder.error instanceof Error
                ? setDeliveryHolder.error.message
                : 'Could not save your delivery workspace'}
            </p>
          )}
          {savedHolderId && !setDeliveryHolder.isPending && !setDeliveryHolder.isError && (
            <p className="text-xs text-muted-foreground">
              Your signed copy will be saved to this workspace.
            </p>
          )}
        </>
      )}
    </section>
  );
}

/**
 * Initials from a display name. "John Doe" → "JD", "Mary Anne Smith" → "MAS",
 * "cher" → "C". Splits on whitespace, takes the first letter of each part,
 * uppercases, and caps at 4 characters so an absurd middle-name chain
 * doesn't overflow the initial-field box. Returns an empty string for an
 * empty / whitespace-only input — the pad's "Type" tab then just renders
 * empty, which is the right cue that the recipient should type their own.
 */
function deriveInitials(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .map((p) => p.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter(Boolean);
  if (parts.length === 0) return '';
  return parts
    .map((p) => p.charAt(0).toUpperCase())
    .slice(0, 4)
    .join('');
}

function CenteredMessage({ title, description }: { title: string; description?: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="max-w-md text-center space-y-2">
        <h1 className="text-xl font-semibold">{title}</h1>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>
    </div>
  );
}

/**
 * Post-submit confirmation — also shown when a recipient returns to their
 * signing link after signing. Polls the recipient's sealed-download endpoint
 * every 5s until it returns a presigned URL. The seal pipeline runs after
 * submitRecipient commits, so on a parallel envelope this resolves within a
 * few seconds; until every party has signed it shows the "available once
 * everyone has signed" copy and a re-check button.
 */
function SubmittedConfirmation({
  token,
  branding,
}: {
  token: string;
  branding?: DocumentSignPublicBranding | null;
}) {
  const sealed = useRecipientSealedDownload(token, { refetchInterval: 5_000 });
  // A recipient returning to their link after signing never loads a session
  // (the strict resolver returns terminal), so branding is fetched on its own
  // here rather than being absent exactly when the closing message matters.
  const fetched = useSigningBranding(token, { enabled: branding === undefined });
  const effective = branding ?? fetched.data?.branding ?? null;
  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="max-w-md text-center space-y-3">
        <h1 className="text-xl font-semibold">Thanks for signing</h1>
        <p className="text-sm text-muted-foreground">
          Your signature has been recorded. Keep this link — once everyone has signed, your
          completed copy will be available to download here.
        </p>
        {sealed.data && (
          <a
            href={sealed.data.downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-medium underline"
          >
            Download your signed copy
          </a>
        )}
        {!sealed.data && (
          <Button variant="outline" size="sm" onClick={() => sealed.refetch()}>
            Check again
          </Button>
        )}
        {/* The sender's own closing message — the point of the screen for
            them, and the last moment a signer is paying attention. */}
        <BrandedCompletionMessage branding={effective} />
      </div>
    </div>
  );
}
